import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  type?: ToastType;
  onDismiss: () => void;
  /** Pins the toast to the bottom of its nearest positioned ancestor instead of the viewport.
   * For errors raised from inside a panel (a block editor's capture/paste failures), a
   * viewport-fixed toast lands at the bottom of the whole task, far from the field that caused
   * it — the message and its cause end up on opposite sides of the screen. Anchored mode keeps
   * them together. Off by default, so every page-level caller (uploads, saves, imports) keeps
   * the existing viewport-centred behaviour unchanged; the container must be `relative`. */
  anchored?: boolean;
}

const variants: Record<ToastType, { icon: React.ReactNode; iconClass: string; bg: string; titleKey: string }> = {
  success: { icon: <CheckCircle2 className='h-5 w-5' />, iconClass: 'text-ws-success-fg', bg: 'bg-ws-success-bg', titleKey: 'toast.success' },
  error:   { icon: <XCircle      className='h-5 w-5' />, iconClass: 'text-ws-error-fg',   bg: 'bg-ws-error-bg',   titleKey: 'toast.error' },
  warning: { icon: <AlertTriangle className='h-5 w-5' />, iconClass: 'text-ws-warning-fg', bg: 'bg-ws-warning-bg', titleKey: 'toast.warning' },
  info:    { icon: <Info          className='h-5 w-5' />, iconClass: 'text-ws-neutral-fg', bg: 'bg-ws-neutral-bg', titleKey: 'toast.info' },
};

export default function Toast({ message, type = 'info', onDismiss, anchored = false }: ToastProps) {
  const { t } = useTranslation();
  const dismissRef = useRef(onDismiss);
  useEffect(() => { dismissRef.current = onDismiss; });

  useEffect(() => {
    const timer = setTimeout(() => dismissRef.current(), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const { icon, iconClass, bg, titleKey } = variants[type];
  const urgent = type === 'error' || type === 'warning';

  return (
    <div
      role={urgent ? 'alert' : 'status'}
      aria-live={urgent ? 'assertive' : 'polite'}
      aria-atomic='true'
      className={`${anchored ? 'absolute bottom-4' : 'fixed bottom-6'} left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-lg border border-ws-border w-[calc(100%-2rem)] max-w-[388px] ${bg} pr-6 pl-4 py-4 shadow-md`}
    >
      <span className={iconClass + ' pr-2'}>{icon}</span>
      <div className='flex flex-col grow min-w-0'>
        <span className='text-sm font-medium text-ws-text'>{t(titleKey)}</span>
        <span className='text-sm text-ws-muted break-words'>{message}</span>
      </div>
      <button onClick={onDismiss} aria-label={t('toast.dismiss')} className='ml-2 text-ws-muted hover:text-ws-text'>
        <X className='h-3.5 w-3.5' />
      </button>
    </div>
  );
}
